// resources/js/session-chat.js
import { io } from 'socket.io-client';

/**
 * session-chat frontend
 *
 * Assumptions:
 *  - Blade sets window.__CHAT_DATA__ (as you already do)
 *  - socket.io-client is available (bundle via Vite/Mix)
 *  - Node socket server is proxied at same origin path '/socket' (Nginx)
 *
 * Uses data attributes exactly as in your Blade:
 *  data-session-chat
 *  data-session-chat-container
 *  data-session-chat-header
 *  data-session-chat-content
 *  data-session-chat-messages
 *  data-session-chat-panel
 *  data-session-chat-input
 *  data-session-chat-send
 *  data-session-chat-template-message (template element)
 *
 */

const DATA_ATTR = 'data-session-chat';
const sel = (s) => `[${s}]`;
const rootSelector = sel(DATA_ATTR);

// helpers
const makeEl = (tag, cls = '') => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    return el;
};
const nowISO = () => new Date().toISOString();
const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function generateTempId() {
    return `tmp_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
}

export function initSessionChat(root = null) {
    const rootNode = root || document.querySelector(rootSelector);
    if (!rootNode) return null;

    const data = window.__CHAT_DATA__ || {};
    const sessionId = data.session_id || (data.sessionData && data.sessionData.id);
    const currentUser = (data.currentUser) ? data.currentUser : null;
    const history = Array.isArray(data.messages) ? data.messages : [];
    const participants = (data.sessionData && data.sessionData.users) || [];

    // DOM nodes
    const container = rootNode.querySelector(sel(`${DATA_ATTR}-container`));
    const messagesWrap = rootNode.querySelector(sel(`${DATA_ATTR}-messages`));
    const inputWrap = rootNode.querySelector(sel(`${DATA_ATTR}-input`));
    const inputEl = inputWrap ? inputWrap.querySelector(sel(`${DATA_ATTR}-input`)) || inputWrap.querySelector('input') : null;
    const sendBtn = rootNode.querySelector(sel(`${DATA_ATTR}-send`));
    const template = document.querySelector(sel(`${DATA_ATTR}-template-message`));
    const MESSAGE_TEMPLATE = template ? template.content.cloneNode(true) : null;

    // storage
    const messagesMap = new Map(); // key: id or tempId -> { id, tempId, dom, data }
    const orderedKeys = []; // preserve order

    // socket
    const socketOpts = {
        path: '/socket', // NB: ensure nginx proxies /socket/* to node
        transports: ['websocket', 'polling'],
        // auth: we rely on cookie (same-origin) — no token required in handshake
    };
    const socket = io(window.location.origin, socketOpts);

    // --- rendering ---
    function createMessageDOM(message) {
        // message: { id?, tempId?, sender_application_id, sender_user_id, sender_name, content, created_at, status }
        if (!MESSAGE_TEMPLATE) {
            // fallback simple DOM
            const wrapper = makeEl('div', 'session-chat__message');
            wrapper.innerHTML = `<div class="session-chat__message-text"><div class="session-chat__message-name">${message.sender_name || ''}</div><div class="session-chat__message-text">${escapeHtml(message.content || '')}</div><div class="session-chat__message-time">${formatTime(message.created_at)}</div><div class="session-chat__message-status">${message.status || ''}</div></div>`;
            return wrapper;
        }

        // build from template
        const tpl = document.querySelector(sel(`${DATA_ATTR}-template-message`));
        const node = tpl.content.firstElementChild.cloneNode(true);

        // find subnodes by attributes
        // inside template your attributes: data-session-chat-message-name, data-session-chat-message-text (duplicate name inside), etc.
        const nameNode = node.querySelector(sel(`${DATA_ATTR}-message-name`));
        const textNode = node.querySelectorAll(`[${DATA_ATTR}-message-text]`); // there are two with same attr - take the second for actual text
        const timeNode = node.querySelector(sel(`${DATA_ATTR}-message-time`));
        const statusNode = node.querySelector(sel(`${DATA_ATTR}-message-status`));

        if (nameNode) nameNode.textContent = message.sender_name || (message.sender_user_id ? `#${message.sender_user_id}` : 'Неизвестно');
        if (textNode && textNode.length > 1) textNode[1].textContent = message.content || '';
        else if (textNode && textNode.length === 1) textNode[0].textContent = message.content || '';
        if (timeNode) timeNode.textContent = formatTime(message.created_at);
        if (statusNode) statusNode.textContent = statusLabel(message.status);

        // add classes for own messages
        const isMine = (currentUser && (String(message.sender_user_id) === String(currentUser.id) || String(message.sender_application_id) === String(currentUser.application_id)));
        if (isMine) node.classList.add('session-chat__message--mine');

        return node;
    }

    function statusLabel(s) {
        switch (s) {
            case 'pending':
                return 'Отправляется…';
            case 'sent':
                return 'Отправлено';
            case 'delivered':
                return 'Доставлено';
            case 'read':
                return 'Прочитано';
            default:
                return '';
        }
    }

    function escapeHtml(text) {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    function renderMessage(message, insertAtEnd = true) {
        const key = message.id || message.tempId;
        if (!key) return null;
        if (messagesMap.has(key)) {
            // update DOM (status/time/content)
            const entry = messagesMap.get(key);
            entry.data = { ...entry.data, ...message };
            // update fields in DOM
            const statusNode = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
            if (statusNode) statusNode.textContent = statusLabel(message.status);
            const textNodes = entry.dom.querySelectorAll(`[${DATA_ATTR}-message-text]`);
            if (textNodes && textNodes.length > 1) textNodes[1].textContent = message.content || '';
            return entry.dom;
        }

        const dom = createMessageDOM(message);
        // store
        messagesMap.set(key, { id: message.id, tempId: message.tempId, dom, data: message });
        orderedKeys.push(key);
        if (insertAtEnd) messagesWrap.appendChild(dom);
        else messagesWrap.insertBefore(dom, messagesWrap.firstChild);

        // auto-scroll near bottom
        if (shouldScrollToBottom()) {
            scrollToBottom();
        }
        return dom;
    }

    function shouldScrollToBottom() {
        const tolerance = 120; // px
        const { scrollTop, scrollHeight, clientHeight } = messagesWrap;
        return (scrollHeight - (scrollTop + clientHeight) < tolerance);
    }

    function scrollToBottom() {
        messagesWrap.scrollTop = messagesWrap.scrollHeight;
    }

    // --- load history ---
    function loadHistory() {
        history.forEach(m => {
            const msg = {
                id: m.id,
                tempId: null,
                sender_application_id: m.sender_application_id,
                sender_user_id: m.sender_user_id,
                sender_name: m.sender_name || (m.sender_user_id ? `#${m.sender_user_id}` : ''),
                content: m.content,
                created_at: m.created_at || nowISO(),
                status: m.read_at ? 'read' : (m.delivered_at ? 'delivered' : 'sent'),
            };
            renderMessage(msg, true);
        });
        // after history, scroll to bottom
        scrollToBottom();
    }

    // --- socket handlers ---
    socket.on('connect', () => {
        // join room with user's role
        const role = (currentUser && currentUser.role) ? currentUser.role : undefined;
        socket.emit('join_room', { roomId: sessionId, role }, (ack) => {
            // ack maybe {ok:true}
            // console.debug('join_room ack', ack);
        });
    });

    socket.on('connected', (payload) => {
        // server welcome
        // console.debug('socket connected payload', payload);
    });

    // incoming message from server (other participant)
    // payload: { message }
    socket.on('message', ({ message }) => {
        // adapt message shape
        const payload = {
            id: message.id,
            sender_application_id: message.sender_id, // if server sends application id later - still ok
            sender_user_id: message.sender_id,
            sender_name: message.sender_name || '',
            content: message.content,
            created_at: message.created_at || nowISO(),
            status: 'sent',
        };
        // render and ack
        renderMessage(payload, true);
        // send delivery ack back
        if (message.id) {
            socket.emit('message_ack', { messageId: message.id, roomId: sessionId });
            // try to mark read if visible (handled by observer)
        }
    });

    // server confirms message accepted by server and returns server id (optimistic flow)
    // payload: { message } where message.temp_id and message.id
    socket.on('message_sent', ({ message }) => {
        // if server returned temp_id -> replace
        if (message.temp_id) {
            const tmpKey = message.temp_id;
            const entry = messagesMap.get(tmpKey);
            if (entry) {
                // set id, update key mapping
                entry.data.id = message.id;
                entry.data.tempId = null;
                entry.data.status = 'sent';
                // update DOM status
                const statusNode = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
                if (statusNode) statusNode.textContent = statusLabel('sent');
                // migrate key
                messagesMap.delete(tmpKey);
                messagesMap.set(message.id, entry);
                // keep orderedKeys consistent
                const idx = orderedKeys.indexOf(tmpKey);
                if (idx !== -1) orderedKeys[idx] = message.id;
            } else {
                // not found (possible), just render
                renderMessage({
                    id: message.id,
                    sender_application_id: message.sender_id,
                    sender_user_id: message.sender_id,
                    sender_name: message.sender_name || '',
                    content: message.content,
                    created_at: message.created_at || nowISO(),
                    status: 'sent',
                }, true);
            }
        } else if (message.id) {
            // server-created message (unlikely for sender) — ensure exists
            if (!messagesMap.has(message.id)) {
                renderMessage({
                    id: message.id,
                    sender_application_id: message.sender_id,
                    sender_user_id: message.sender_id,
                    sender_name: message.sender_name || '',
                    content: message.content,
                    created_at: message.created_at || nowISO(),
                    status: 'sent',
                }, true);
            }
        }
    });

    socket.on('message_delivered', ({ messageId }) => {
        if (!messageId) return;
        const entry = messagesMap.get(messageId);
        if (entry) {
            entry.data.status = 'delivered';
            const st = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
            if (st) st.textContent = statusLabel('delivered');
        }
    });

    socket.on('message_read', ({ messageId, userId }) => {
        if (!messageId) return;
        const entry = messagesMap.get(messageId);
        if (entry) {
            entry.data.status = 'read';
            const st = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
            if (st) st.textContent = statusLabel('read');
        }
    });

    // presence events
    socket.on('participant_joined', (d) => {
        // optional: show presence toast / update header
        // console.debug('participant_joined', d);
    });
    socket.on('participant_left', (d) => {
        // console.debug('participant_left', d);
    });

    // --- send message flow (optimistic) ---
    function sendMessage(text) {
        if (!text || !text.trim()) return;
        const tempId = generateTempId();
        const now = nowISO();

        const optimistic = {
            id: null,
            tempId,
            sender_application_id: currentUser.application_id || null,
            sender_user_id: currentUser.id || null,
            sender_name: currentUser.name || 'Вы',
            content: text,
            created_at: now,
            status: 'pending',
        };

        // render locally
        renderMessage(optimistic, true);

        // emit to socket
        socket.emit('send_message', {
            roomId: sessionId,
            tempId,
            content: text,
            type: 'text',
            user_application_id: currentUser.application_id || null, // node may use this if implemented
        }, (ack) => {
            // ack might be {ok:true, messageId, delivered}
            const entry = messagesMap.get(tempId);
            if (entry) {
                if (ack && ack.ok) {
                    entry.data.status = ack.delivered ? 'delivered' : 'sent';
                    const st = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
                    if (st) st.textContent = statusLabel(entry.data.status);
                } else {
                    // failure -> set pending and maybe show error style
                    entry.data.status = 'pending';
                    const st = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
                    if (st) st.textContent = statusLabel('pending');
                }
            }
        });

        // clear input
        if (inputEl) inputEl.value = '';
        scrollToBottom();
    }

    if (sendBtn) {
        sendBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (!inputEl) return;
            sendMessage(inputEl.value);
        });
    }
    if (inputEl) {
        inputEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(inputEl.value);
            }
        });
    }

    // --- read detection: IntersectionObserver for messages area ---
    const ioOptions = { root: messagesWrap, threshold: 0.8 };
    const intersectionObserver = new IntersectionObserver(entries => {
        for (const ent of entries) {
            if (!ent.isIntersecting) continue;
            const node = ent.target;
            const key = findKeyByDom(node);
            if (!key) continue;
            const entry = messagesMap.get(key);
            if (!entry) continue;
            // if message not from me and not yet read -> send read
            const mine = String(entry.data.sender_user_id) === String(currentUser.id) || String(entry.data.sender_application_id) === String(currentUser.application_id);
            if (!mine && entry.data.status !== 'read' && entry.data.id) {
                socket.emit('message_read', { messageId: entry.data.id, roomId: sessionId });
                entry.data.status = 'read';
                const st = entry.dom.querySelector(sel(`${DATA_ATTR}-message-status`));
                if (st) st.textContent = statusLabel('read');
            }
        }
    }, ioOptions);

    // observe added messages (attach observer to message doms)
    function observeDomMessages() {
        messagesMap.forEach((v, key) => {
            try {
                intersectionObserver.observe(v.dom);
            } catch (e) {}
        });
    }

    function findKeyByDom(dom) {
        for (const [k, v] of messagesMap.entries()) {
            if (v.dom === dom) return k;
        }
        return null;
    }

    // observe container mutations to add new nodes to observer
    const mo = new MutationObserver((mut) => {
        for (const m of mut) {
            for (const n of m.addedNodes) {
                if (n.nodeType === 1) {
                    const key = findKeyByDom(n);
                    if (key) {
                        try { intersectionObserver.observe(n); } catch (e) {}
                    }
                }
            }
        }
    });
    mo.observe(messagesWrap, { childList: true });

    // initial load
    loadHistory();
    observeDomMessages();

    // expose simple API for debugging
    return {
        socket,
        sendMessage,
        rootNode,
        getMessagesOrdered: () => orderedKeys.map(k => messagesMap.get(k)?.data).filter(Boolean),
    };
}

// auto init if present
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSessionChat());
} else {
    initSessionChat();
}
